import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaPills, FaPenSquare } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { CircularProgress } from "@mui/material";
import { API_URL } from "../../api";

function Rewards() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: API_URL(),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [medicineLists, setMedicineLists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openReward, setOpenReward] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rewardDesc, setRewardDesc] = useState("");
  const [rewardImage, setRewardImage] = useState(null);
  const [preview, setPreview] = useState("");

  const getRewardMedicines = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("rewards/medicine");
      setMedicineLists(res.data?.list || []);
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRewardMedicines();
  }, []);

  const handleOpenReward = (item) => {
    setSelectedItem(item);
    setRewardDesc(item?.description || "");
    setPreview(item?.rewardImage || "");
    setRewardImage(null);
    setOpenReward(true);
  };

  const handleCloseReward = () => {
    setOpenReward(false);
    setSelectedItem(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRewardImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSaveReward = async () => {
    if (!selectedItem) return;
    const formData = new FormData();
    formData.append("id", selectedItem._id);
    if (rewardDesc) formData.append("description", rewardDesc);
    if (rewardImage) formData.append("rewardImage", rewardImage);
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };
    try {
      await api.put("medicine-list", formData, config);
      Swal.fire({
        title: "Successful!",
        text: "Reward Updated Successfully!",
        icon: "success",
        cancelButtonText: "Close",
      });
      handleCloseReward();
      getRewardMedicines();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  const handleUnitClick = (medicine) => {
    navigate(`/rewards/medicine-items/${medicine._id}`, {
      state: { name: medicine.name },
    });
  };

  return (
    <div className="App">
      <div className="wrapper">
        <div style={{ paddingBottom: "1.5rem" }}>
          <div className="content-header">
            <div className="container-fluid px-2">
              <div className="row mb-2">
                <div className="col-sm-12">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item active">Rewards</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="row px-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <FaPills /> Medicine Rewards
                  </h3>
                </div>

                <div className="card-body">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <CircularProgress />
                    </div>
                  ) : (
                    <table id="medicineTable" className="table">
                      <thead className="text-center bg-info">
                        <tr>
                          <th>No</th>
                          <th>Code</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Sub Category</th>
                          <th>Image</th>
                          <th>Description</th>
                          <th style={{ whiteSpace: "nowrap", width: "1%" }}>Unit</th>
                          <th style={{ whiteSpace: "nowrap", width: "1%" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody className="text-center">
                        {medicineLists.length > 0 ? (
                          medicineLists.map((item, i) => (
                            <tr key={item._id || i}>
                              <td>{i + 1}</td>
                              <td>{item.code}</td>
                              <td>{item.name}</td>
                              <td>{item.relatedCategory?.name || item.relatedCategory || "-"}</td>
                              <td>{item.relatedSubCategory?.name || item.relatedSubCategory || "-"}</td>
                              <td>
                                {item.rewardImage ? (
                                  <img
                                    src={item.rewardImage}
                                    alt="reward"
                                    style={{
                                      maxWidth: "60px",
                                      maxHeight: "40px",
                                      borderRadius: "4px",
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{item.description || "-"}</td>
                              <td style={{ whiteSpace: "nowrap", width: "1%" }}>
                                <button
                                  className="btn btn-primary btn-sm px-3"
                                  onClick={() => handleUnitClick(item)}
                                >
                                  Unit
                                </button>
                              </td>
                              <td style={{ whiteSpace: "nowrap", width: "1%" }}>
                                <button
                                  className="btn btn-warning btn-sm"
                                  onClick={() => handleOpenReward(item)}
                                >
                                  <FaPenSquare />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={9} className="text-center">
                              No medicine rewards available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={openReward} onClose={handleCloseReward} maxWidth="sm" fullWidth>
          <DialogTitle>
            <div className="modal-header bg-info">
              <h4 className="modal-title">
                Reward Details - {selectedItem?.name}
              </h4>
              <button
                type="button"
                className="close"
                onClick={handleCloseReward}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </DialogTitle>
          <DialogContent>
            <div className="modal-body">
              <div class="form-group">
                <label>Image</label>
                <input
                  type="file"
                  className="form-control border-info"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {preview && (
                  <div className="mt-2">
                    <img
                      src={preview}
                      alt="Preview"
                      style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "4px" }}
                    />
                  </div>
                )}
              </div>
              <div class="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={rewardDesc}
                  onChange={(e) => setRewardDesc(e.target.value)}
                  placeholder="Enter reward description..."
                ></textarea>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <button className="btn btn-secondary" onClick={handleCloseReward}>
              Cancel
            </button>
            <button className="btn btn-info" onClick={handleSaveReward}>
              Save
            </button>
          </DialogActions>
        </Dialog>

        <aside className="control-sidebar control-sidebar-dark"></aside>
      </div>
    </div>
  );
}

export default Rewards;